/**
 * SiteWorkflowNotes Component
 * Displays workflow notes/remarks from phase operations, status changes, and attendance
 * Similar to ProjectProgressPanel's workflow notes section
 */

import React, { useState, useMemo } from 'react';
import { SITE_HISTORY_CHANGE_TYPE } from '../../utils/constants';

const SiteWorkflowNotes = ({ history = [], loading = false }) => {
  const [showAll, setShowAll] = useState(false);

  // Extract and combine all workflow notes from history
  const workflowNotes = useMemo(() => {
    return history
      .filter(item => {
        // Only include items that have notes or reasons in metadata
        if (item.metadata?.notes || item.metadata?.reason) return true;
        // Include status changes which often have important context
        if (item.changeType === SITE_HISTORY_CHANGE_TYPE.STATUS_CHANGED && item.metadata?.reason) return true;
        return false;
      })
      .map(item => {
        const changeType = item.changeType;
        const userName = item.changedByUser?.nickname || item.changedByUser?.fullName || 'Unknown';

        // Determine event type and styling
        let eventType = 'other';
        let category = 'General';
        let label = 'Note';

        switch (changeType) {
          case SITE_HISTORY_CHANGE_TYPE.PHASE_COMPLETED:
            eventType = 'phase_completed';
            category = 'Phase';
            label = 'Completed';
            break;
          case SITE_HISTORY_CHANGE_TYPE.PHASE_SKIPPED:
            eventType = 'phase_skipped';
            category = 'Phase';
            label = 'Skipped';
            break;
          case SITE_HISTORY_CHANGE_TYPE.PHASE_PROGRESS_UPDATED:
            eventType = 'progress_update';
            category = 'Phase';
            label = 'Progress Update';
            break;
          case SITE_HISTORY_CHANGE_TYPE.STATUS_CHANGED:
            eventType = 'status_changed';
            category = 'Status';
            label = 'Status Change';
            break;
          case SITE_HISTORY_CHANGE_TYPE.NOTES_UPDATED:
            eventType = 'notes_updated';
            category = 'Notes';
            label = item.metadata?.noteType ?
              item.metadata.noteType.charAt(0).toUpperCase() + item.metadata.noteType.slice(1) :
              'Updated';
            break;
          case SITE_HISTORY_CHANGE_TYPE.ATTENDANCE_CHECK_IN:
            eventType = 'attendance';
            category = 'Attendance';
            label = 'Check-In';
            break;
          case SITE_HISTORY_CHANGE_TYPE.ATTENDANCE_CHECK_OUT:
            eventType = 'attendance';
            category = 'Attendance';
            label = 'Check-Out';
            break;
          default:
            break;
        }

        const notes = item.metadata?.notes || item.metadata?.reason || '';

        return {
          id: item.id,
          timestamp: item.timestamp || item.createdAt,
          eventType,
          category,
          label,
          notes,
          userName,
          description: item.description,
          phaseName: item.metadata?.phaseName || item.relatedPhase?.name || null
        };
      })
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [history]);

  // Get styling based on event type
  const getEventStyles = (eventType) => {
    switch (eventType) {
      case 'phase_completed':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-900',
          categoryBg: 'bg-green-100 text-green-700',
          labelBg: 'bg-green-100 text-green-700'
        };
      case 'phase_skipped':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-900',
          categoryBg: 'bg-yellow-100 text-yellow-700',
          labelBg: 'bg-yellow-100 text-yellow-700'
        };
      case 'progress_update':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-900',
          categoryBg: 'bg-blue-100 text-blue-700',
          labelBg: 'bg-cyan-100 text-cyan-700'
        };
      case 'status_changed':
        return {
          bg: 'bg-purple-50',
          border: 'border-purple-200',
          text: 'text-purple-900',
          categoryBg: 'bg-purple-100 text-purple-700',
          labelBg: 'bg-purple-100 text-purple-700'
        };
      case 'notes_updated':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          text: 'text-amber-900',
          categoryBg: 'bg-amber-100 text-amber-700',
          labelBg: 'bg-amber-100 text-amber-700'
        };
      case 'attendance':
        return {
          bg: 'bg-teal-50',
          border: 'border-teal-200',
          text: 'text-teal-900',
          categoryBg: 'bg-teal-100 text-teal-700',
          labelBg: 'bg-teal-100 text-teal-700'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-900',
          categoryBg: 'bg-gray-100 text-gray-700',
          labelBg: 'bg-gray-100 text-gray-700'
        };
    }
  };

  const getActionLabel = (eventType) => {
    switch (eventType) {
      case 'phase_completed': return 'Completed by';
      case 'phase_skipped': return 'Skipped by';
      case 'progress_update': return 'Updated by';
      case 'status_changed': return 'Changed by';
      case 'notes_updated': return 'Updated by';
      case 'attendance': return 'Recorded by';
      default: return 'By';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (workflowNotes.length === 0) {
    return null; // Don't show section if no notes
  }

  const displayedNotes = showAll ? workflowNotes : workflowNotes.slice(0, 3);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Workflow Notes
        <span className="text-sm text-gray-500 font-normal">({workflowNotes.length} entries)</span>
      </h2>

      <div className="space-y-3">
        {displayedNotes.map((note, index) => {
          const styles = getEventStyles(note.eventType);
          const actionLabel = getActionLabel(note.eventType);

          return (
            <div
              key={note.id || index}
              className={`p-3 rounded-lg border ${styles.bg} ${styles.border}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles.categoryBg}`}>
                    {note.category}
                  </span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles.labelBg}`}>
                    {note.label}
                  </span>
                  {note.phaseName && (
                    <span className="text-xs text-gray-600">
                      {note.phaseName}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {new Date(note.timestamp).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>

              {note.notes ? (
                <p className={`text-sm whitespace-pre-wrap ${styles.text}`}>
                  {note.notes}
                </p>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  No notes provided
                </p>
              )}

              <p className="text-xs text-gray-500 mt-1">
                {actionLabel}: {note.userName}
              </p>
            </div>
          );
        })}

        {workflowNotes.length > 3 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
          >
            {showAll ? (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                Show less
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                View {workflowNotes.length - 3} more entries
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default SiteWorkflowNotes;
